import React from 'react';
import { useTranslation } from 'react-i18next';
import { resolveLegacyTaskType } from 'core/slices/utils/constants';
import { Autocomplete, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  footer: {
    bgcolor: 'background.paper',
    borderTop: '1px solid', borderColor: 'divider',
    px: 2.5, py: 1.5, flexShrink: 0, width: '100%',
    display: 'flex', flexDirection: 'column',
    gap: 1.5,
  },
  taskSelectorsRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 1.5,
  },
  selectorWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  sectionLabel: {
    fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: 'text.disabled', ml: 0.25, mb: 0.75, display: 'block',
  },
  autocompleteStyle: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: 'background.paper',
      height: 40,
      '& fieldset': { borderColor: 'divider' },
      '&:hover fieldset': { borderColor: 'text.disabled' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '1px' },
      '& .MuiOutlinedInput-input': { py: 0 },
    },
    '& .MuiInputBase-input': { fontWeight: 400, fontSize: '0.8rem', color: 'text.primary' },
  },
  taskActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
  },
  generateBtn: {
    borderRadius: 2,
    px: 4,
    py: 1,
    fontWeight: 700,
    bgcolor: 'primary.main',
    color: '#fff',
    '&:hover': { bgcolor: 'primary.main', filter: 'brightness(0.92)' },
    '&:disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
    boxShadow: 'none',
  },
};

// ── TaskFooter ────────────────────────────────────────────────────────────────

export default function TaskFooter({
  searchResults,
  selectedProduct,
  taskType,
  aisle,
  resourcesReportOptions,
  aislesList,
  loadingReport,
  onSelectProduct,
  onTaskTypeChange,
  onAisleChange,
  onReset,
  onCreateTask,
}) {
  const { t } = useTranslation();
  const noTaskOptions = !resourcesReportOptions || resourcesReportOptions.length === 0;
  const noAisleOptions = !aislesList || aislesList.length === 0;

  return (
    <Box sx={styles.footer}>
      {/* Product picker — shown when multiple results */}
      {searchResults?.length > 1 && (
        <Box sx={styles.selectorWrapper}>
          <Typography sx={styles.sectionLabel}>
            {t('bruno.scanner.select_product', 'Select product')}
          </Typography>
          <Autocomplete
            fullWidth
            options={searchResults}
            getOptionLabel={(option) => `${option.description} · ${t('bruno.products.item', 'Item')} ${option.item}`}
            value={selectedProduct}
            onChange={(_, value) => value ? onSelectProduct(value) : onSelectProduct(null)}
            size="small"
            noOptionsText={t('bruno.scanner.no_option', 'No option')}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('bruno.scanner.select_product', 'Select product')}
                sx={styles.autocompleteStyle}
              />
            )}
          />
        </Box>
      )}

      {/* Selectors row */}
      <Box sx={styles.taskSelectorsRow}>
        {/* Task type */}
        <Box sx={styles.selectorWrapper}>
          <Typography sx={styles.sectionLabel}>
            {t('bruno.camera.task_type', 'Task type')}
          </Typography>
          <Autocomplete
            fullWidth
            options={resourcesReportOptions || []}
            getOptionLabel={(option) => t(`bruno.tasks_names.${resolveLegacyTaskType(option.task_type)}`, option.task_type)}
            value={resourcesReportOptions?.find(opt => opt.task_type === taskType) || null}
            onChange={(_, value) => onTaskTypeChange(value?.task_type || '')}
            disabled={noTaskOptions}
            size="small"
            noOptionsText={t('bruno.scanner.no_option', 'No option')}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={noTaskOptions ? t('bruno.scanner.no_option', 'No option') : t('bruno.tasks.modal_title', 'Select type')}
                sx={styles.autocompleteStyle}
              />
            )}
          />
        </Box>

        {/* Aisle */}
        <Box sx={styles.selectorWrapper}>
          <Typography sx={styles.sectionLabel}>
            {t('bruno.camera.aisle_title', 'Aisle')}
          </Typography>
          <Autocomplete
            fullWidth
            options={aislesList || []}
            value={aisle || null}
            onChange={(_, value) => onAisleChange(value || '')}
            disabled={noAisleOptions}
            size="small"
            freeSolo
            noOptionsText={t('bruno.scanner.no_option', 'No option')}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={noAisleOptions ? t('bruno.scanner.no_option', 'No option') : t('bruno.scanner.aisle_placeholder', 'Select aisle')}
                sx={styles.autocompleteStyle}
              />
            )}
          />
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={styles.taskActions}>
        <Button onClick={onReset} sx={{ fontWeight: 700, color: 'primary.main' }}>
          {t('bruno.buttons.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={onCreateTask}
          disabled={!taskType || loadingReport || !selectedProduct}
          sx={styles.generateBtn}
        >
          {loadingReport && <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />}
          {t('bruno.camera.scan', 'Generate task')}
        </Button>
      </Box>
    </Box>
  );
}
